import React, { useReducer, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import EventForm from '../components/forms/EventForm';
import * as routes from '../routes/routes';
import { GlobalSettingsContext } from '../firebase/GlobalSettingsContext';
import { CommonDataContext } from '../firebase/CommonDataContext';
import CompetitionDropdown from '../components/forms/Dropdowns/CompetitionDropdown';
import { ROLES } from '../components/misc/Utils';
import { UserDataContext } from '../firebase/UserDataContext';
import { FirebaseNewContext } from '../firebase/FirebaseNewContext';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel, FormGroup, FormLabel, ListItemText, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';

function globalSettingsFormInitialState(props) {
    return {
        selectedCompetition: '',
        globalDefaultCompetition: '',
        showFinalScore: false,
        lockCompetition: false,
        errorMessages: []
    }

}

function globalSettingsFormReducer(state, action) {
    switch (action.type) {
        case 'values_changed':
            return {
                ...state,
                ...action.value
            }
        case 'show_final_score_changed':
            return {
                ...state,
                showFinalScore: !state.showFinalScore
            }
        case 'lock_competition_changed':
            return {
                ...state,
                lockCompetition: !state.lockCompetition
            }
        case 'competition_changed':
            // TODO - will prompt the change of the show final score to change
            // pass the globals required to make this change
            let showFinalScore
            if (('showFinalScoresToTeams' in action.globalSettings)) {
                showFinalScore = action.globalSettings.showFinalScoresToTeams[action.value] || false
            }

            let lockCompetition
            if (('lockCompetition' in action.globalSettings)) {
                lockCompetition = action.globalSettings.lockCompetition[action.value] || false
            }
            return {
                ...state,
                selectedCompetition: action.value,
                showFinalScore,
                lockCompetition
            }
        case 'global_competition_changed':
            console.log('this is our', action.value)
            return {
                ...state,
                globalDefaultCompetition: action.value
            }
        default:
            break;
    }
}

function GlobalSettingsPage(props) {
    const { globalSettings } = useContext(GlobalSettingsContext)
    const { firebase } = useContext(FirebaseNewContext)
    const { defaultSelectedCompetition, user } = useContext(UserDataContext)
    const [state, dispatch] = useReducer(globalSettingsFormReducer, props, globalSettingsFormInitialState)
    const [bulkComp, setBulkComp] = useState('');
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null); // null | number (count) | -1 (error)
    useEffect(() => {
        let showFinalScore = false
        if (('showFinalScoresToTeams' in globalSettings)) {
            showFinalScore = globalSettings.showFinalScoresToTeams[globalSettings.defaultCompetition] || false
        }

        let lockCompetition = false
        if (('lockCompetition' in globalSettings)) {
            lockCompetition = (globalSettings.lockCompetition != undefined && globalSettings.lockCompetition[globalSettings.defaultCompetition]) || false
        }
        dispatch({
            type: 'values_changed',
            value: {
                selectedCompetition: globalSettings['defaultCompetition'],
                globalDefaultCompetition: globalSettings['defaultCompetition'],
                showFinalScore,
                lockCompetition
            }
        })

    }, [])
    const submitButtonPressed = () => {
        firebase.setShowFinalScoresToTeams(state.selectedCompetition, state.showFinalScore)
        firebase.setLockCompetition(state.selectedCompetition, state.lockCompetition);
        firebase.setDefaultCompetition(state.globalDefaultCompetition)
    }
    const handleBulkApply = async () => {
        setBulkDialogOpen(false);
        setBulkLoading(true);
        setBulkResult(null);
        try {
            const count = await firebase.bulkSetCompetitionForUsers(bulkComp);
            setBulkResult(count);
        } catch (e) {
            console.error('bulkSetCompetitionForUsers error', e);
            setBulkResult(-1);
        } finally {
            setBulkLoading(false);
        }
    };
    return (
        <Grid2 container direction="column" sx={{ p: { xs: 2, sm: 3 }, gap: 3, maxWidth: 900, ridth: '100%' }} >
            <Typography variant="h4" sx={{ mb: 1 }}>
                Competition Settings
            </Typography>

            <Card variant="outlined">
                <CardHeader
                    title="Global Default"
                    subheader="Used across the app unless a specific screen overrides it."
                />
                <CardContent>
                    <Stack spacing={1.5}>
                        <FormLabel id="global-default-label">Global default competition</FormLabel>
                        <CompetitionDropdown
                            aria-labelledby="global-default-label"
                            value={state.globalDefaultCompetition}
                            onChange={(value) => {
                                dispatch({ type: 'global_competition_changed', value });
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Changing this doesn’t switch your personal active competition below.
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardHeader
                    title="Active Competition"
                    subheader="Controls which competition you’re working with right now."
                />
                <CardContent>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            Current:&nbsp;
                            <strong>
                                {globalSettings?.competitions?.[state.selectedCompetition] ?? '—'}
                            </strong>
                        </Typography>

                        <FormLabel id="active-competition-label">Select active competition</FormLabel>
                        <CompetitionDropdown
                            aria-labelledby="active-competition-label"
                            value={state.selectedCompetition}
                            onChange={(value) => {
                                dispatch({ type: 'competition_changed', value, globalSettings });
                            }}
                        />
                    </Stack>
                </CardContent>

                <Divider />

                <CardContent>
                    <Stack spacing={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Display & Access
                        </Typography>

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!state.showFinalScore}
                                        onChange={(_, checked) =>
                                            dispatch({ type: 'show_final_score_changed', value: checked })
                                        }
                                        inputProps={{ 'aria-label': 'Show the final score' }}
                                    />
                                }
                                label="Show the final score"
                            />
                        </FormGroup>

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!state.lockCompetition}
                                        onChange={(_, checked) =>
                                            dispatch({ type: 'lock_competition_changed', value: checked })
                                        }
                                        inputProps={{ 'aria-label': 'Lock the competition' }}
                                    />
                                }
                                label="Lock the competition"
                            />
                        </FormGroup>

                        <Alert severity={state.lockCompetition ? 'warning' : 'info'}>
                            {state.lockCompetition
                                ? 'Competition is locked. Changes to the active competition are disabled until unlocked.'
                                : 'Locking prevents accidental changes to the active competition.'}
                        </Alert>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardHeader
                    title="Set Active Competition for All Competitors"
                    subheader="Updates the competition shown for all team, judge, and score committee users."
                />
                <CardContent>
                    <Stack spacing={1.5}>
                        <FormLabel>Target competition</FormLabel>
                        <CompetitionDropdown
                            value={bulkComp}
                            onChange={(value) => { setBulkComp(value); setBulkResult(null); }}
                        />
                        <Box>
                            <Button
                                variant="outlined"
                                color="warning"
                                disabled={!bulkComp || bulkLoading}
                                onClick={() => setBulkDialogOpen(true)}
                            >
                                {bulkLoading ? 'Applying…' : 'Apply to All Team Users'}
                            </Button>
                        </Box>
                        {bulkResult === -1 && <Alert severity="error">Failed — check console.</Alert>}
                        {typeof bulkResult === 'number' && bulkResult >= 0 && (
                            <Alert severity="success">Updated {bulkResult} team user{bulkResult !== 1 ? 's' : ''}.</Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)}>
                <DialogTitle>Apply to All Users?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will set the active competition to <strong>{globalSettings?.competitions?.[bulkComp] ?? bulkComp}</strong> for all team, judge, and score committee users. They will see this competition on their next login.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleBulkApply} color="warning" variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: (t) => t.zIndex.appBar,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    backdropFilter: 'saturate(180%) blur(6px)',
                }}
            >
                <Button type="submit" variant="contained" onClick={submitButtonPressed}>
                    Save
                </Button>
            </Box>
        </Grid2>
    );

}

export default GlobalSettingsPage;
